import keyBy from "lodash/keyBy";
import { globalDefaultLocations, touringDefaultLocations } from "pages/MarketingPage/MusicStreams/TargetingView/data";
import ads from 'assets/images/recommendations/ads.png'
import website from 'assets/images/recommendations/website.png'
import presave from 'assets/images/recommendations/presave.png'
import { FontThemeStyle, MemberRole, TargetingAudienceGroup } from "modules/Types";
import Colors from "./Colors";


/**
 * Shared audience configurations for campaign targeting
 */
export const SHARED_AUDIENCE_CONFIGS = {
    /// Current fans audience group with related tags 
    CURRENT_FANS: {
        id: 'currentFans',
        title: 'FB + IG Engagers',
        description: 'Target people who have engaged with your Facebook Page and Instagram posts in the last 365 days.',
        tags: [
            { id: 'fb_engagers', title: 'Facebook Page Followers', type: 'presetAudience' as const },
            { id: 'ig_engagers', title: 'Instagram Followers', type: 'presetAudience' as const },
        ]
    },
    /// Lookalike audience group with related tags 
    SIMILAR_FANS: {
        id: 'lookalikes',
        title: 'IG Engager Lookalikes',
        description: 'Target people who are similar to fans who have engaged with your Instagram posts',
        tags: [
            { id: 'fb_engagers_similar_1', title: 'Fans Similar to your Current Fans', type: 'lookalikeAudience' as const },
        ]
    },
    /// Lookalike audience group with related tags
    CONVERSION_EVENT_LOOKALIKES: {
        id: 'conversionEventLookalikes',
        title: 'Conversion Event Lookalikes',
        description: 'Target people who are similar to fans who have taken your conversion event',
        tags: [
            { id: 'conversion_event_lookalikes_1', title: 'Conversion Event Lookalikes', type: 'lookalikeAudience' as const },
        ]
    }
};

export const TABS = {
    RECORD: "record",
    LINK: "link",
    CREATIVE: "creative",
    TARGETING: "targeting",
    BUDGET: "budget",
    FINALIZATION: "finalization",
};


export const DEFAULT_CURRENCY = "USD";

export const PAYPAL_BILLING_AGREEMENT = 13;

export const CREDIT_CARD = 1;

export const MINIMUM_DAILY_BUDGET_PER_DAY_IN_DOLLARS = 10;
export const MINIMUM_LIFETIME_BUDGET_TIKTOK = 50; // Minimum lifetime budget for TikTok

// NOTE DANIEL When updating a campaign budget facebook requires at least 10% more than whats already spent
export const FACEBOOK_MINIMUM_10_PERCENT_BUDGET_INCREASE = 1.1;

export const PAYMENT_METHODS: Record<number, string> = {
    0: 'UNSET',
    1: 'CREDIT_CARD',
    2: 'FACEBOOK_WALLET',
    3: 'FACEBOOK_PAID_CREDIT',
    4: 'FACEBOOK_EXTENDED_CREDIT',
    5: 'ORDER',
    6: 'INVOICE',
    7: 'FACEBOOK_TOKEN',
    8: 'EXTERNAL_FUNDING',
    9: 'FEE',
    10: 'FX',
    11: 'DISCOUNT',
    12: 'PAYPAL_TOKEN',
    13: 'PAYPAL_BILLING_AGREEMENT',
    14: 'FS_NULL',
    15: 'EXTERNAL_DEPOSIT',
    16: 'TAX',
    17: 'DIRECT_DEBIT',
    18: 'DUMMY',
    19: 'ALTPAY',
    20: 'STORED_BALANCE',
};

// ONE OFF CAMPAIGN PRICE
export const ONE_OFF_CAMPAIGN_PRICE = 10;

// PRO PLAN
// Price per additional user per month for Pro plan accounts
export const PRO_PLAN_ADDITIONAL_SEAT_PRICE = 15;

// Base monthly price for Pro plan subscription
export const PRO_MONTHLY_PLAN_PRICE = 20.00;

// Base yearly price for Pro plan subscription
export const PRO_ANNUAL_PLAN_PRICE = 200.00;

// Monthly price when paying annually (10 months for price of 12)
export const PRO_ANNUAL_BY_MONTH_PLAN_PRICE = PRO_ANNUAL_PLAN_PRICE / 12;

// Monthly ad spend limit before 2% automation fee applies
export const ADDITIONAL_SPEND_BUDGET = 1000;

// Maximum number of additional team members that can be added to a Pro plan
export const PRO_PLAN_INCLUDED_SEATS = 2;

// Previous Pro plan monthly price, used for grandfathered accounts
export const OLD_PLAN_PRICE = 15;

// Monthly price for Lite plan subscription
export const LITE_MONTHLY_PLAN_PRICE = 6;

// Yearly price for Lite plan subscription (10 months for price of 12)
export const LITE_ANNUAL_PLAN_PRICE = 60;

// Monthly cost for an annual Lite plan
export const LITE_ANNUAL_BY_MONTH_PLAN_PRICE = LITE_ANNUAL_PLAN_PRICE / 12;

// Fee charged per campaign for Lite plan
export const LITE_PER_CAMPAIGN_FEE = 10;

// TEAM PLAN
// Base monthly price for team plan subscription
export const TEAM_MONTHLY_PLAN_PRICE = 65.00
// Base yearly price for team plan subscription (10 months for price of 12)
export const TEAM_ANNUAL_PLAN_PRICE = 650.00
export const TEAM_ANNUAL_BY_MONTH_PLAN_PRICE = TEAM_ANNUAL_PLAN_PRICE / 12;

// 2% fee charged on ad spend above the monthly limit
export const TEAM_PLAN_MONTHLY_AD_SPEND_OVERAGE_LIMIT_FEE = 0.02
// Monthly ad spend limit before overage fees apply
export const TEAM_PLAN_MONTHLY_AD_SPEND_LIMIT = 5000;
// Price per additional team member seat beyond included seats
export const TEAM_PLAN_ADDITIONAL_SEAT_PRICE = 10.00
// Price per additional Pro account beyond included accounts
export const TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE = 15.00
// Number of team member seats included in base plan
export const TEAM_PLAN_INCLUDED_SEATS = 3;
// Number of Pro accounts included in base plan
export const TEAM_PLAN_INCLUDED_PRO_ACCOUNTS = 3;

/**
 * Enum representing the different subscription plan types available in the application
 * @enum {string}
 */
export enum PlanType {
    PRO = 'pro',
    TEAM = 'team',
    LITE = 'lite',
    FREE = 'free'
}

/**
 * Interface defining subscription plan details
 * @interface Subscription
 */
interface Subscription {
    title: string
    subtitle: string
    priceMonthly: number
    priceAnnual: number
    priceAnnualByMonth: number
    buttonText: string
    buttonFreeTrialText: string
    perks: Perk[]
}


/**
 * Interface defining a subscription plan perk
 * @interface Perk
 */
export interface Perk {
    /** Main perk description */
    info: string;
    /** Additional detail about the perk */
    detail: string | null;
}


/** Configuration object for all subscription plans */
export const SUBSCRIPTION_BENEFITS: { [key in PlanType]: Subscription } = {
    [PlanType.FREE]: {
        title: 'Free',
        subtitle: 'Get started with a link-in-bio and free marketing tools.',
        priceMonthly: 0,
        priceAnnual: 0,
        priceAnnualByMonth: 0,
        buttonText: 'Get Started',
        buttonFreeTrialText: 'Get Started',
        perks: [
            {
                info: 'Create Websites & Pre-saves',
                detail: null,
            },
            {
                info: 'Automated Marketing',
                detail: '$10 / campaign',
            },
            {
                info: 'Fanbase Analytics',
                detail: 'Last 7 days',
            },
        ],
    },
    [PlanType.LITE]: {
        title: 'Lite',
        subtitle: 'Built for creators who want full link-in-bio customization and analytics.',
        priceMonthly: LITE_MONTHLY_PLAN_PRICE,
        priceAnnual: LITE_ANNUAL_PLAN_PRICE,
        priceAnnualByMonth: LITE_ANNUAL_BY_MONTH_PLAN_PRICE,
        buttonText: 'Get Started',
        buttonFreeTrialText: 'Start 7-Day Free Trial',
        perks: [
            {
                info: 'Website Customization',
                detail: 'Unlimited',
            },
            {
                info: 'Forever Saves + Pre-save Boosts',
                detail: null,
            },
            {
                info: 'Website Analytics',
                detail: 'Unlimited',
            },
            {
                info: 'Automated Marketing',
                detail: '$10 / campaign',
            },
            {
                info: 'Fanbase Analytics',
                detail: 'Last 30 days',
            },
        ],
    },
    [PlanType.PRO]: {
        title: 'Pro',
        subtitle: 'For creators who are ready to run advanced marketing campaigns.',
        priceMonthly: PRO_MONTHLY_PLAN_PRICE,
        priceAnnual: PRO_ANNUAL_PLAN_PRICE,
        priceAnnualByMonth: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
        buttonText: 'Get Started',
        buttonFreeTrialText: 'Start 7-Day Free Trial',
        perks: [
            {
                info: 'Everything in Lite',
                detail: null,
            },
            {
                info: 'Automated Marketing',
                detail: 'Unlimited',
            },
            {
                info: 'Fanbase Analytics',
                detail: 'Lifetime',
            },
            {
                info: 'Full Access to AI Tools',
                detail: null,
            },
            {
                info: 'Downloadable Reports',
                detail: null,
            },
            {
                info: 'Invite your Team',
                detail: "+$15 / user",
            },
        ],
    },
    [PlanType.TEAM]: {
        title: 'Teams',
        subtitle: 'Built for teams to scale their marketing efforts across multiple profiles',
        priceMonthly: TEAM_MONTHLY_PLAN_PRICE,
        priceAnnual: TEAM_ANNUAL_PLAN_PRICE,
        priceAnnualByMonth: TEAM_ANNUAL_BY_MONTH_PLAN_PRICE,
        buttonText: 'Get Started',
        buttonFreeTrialText: 'Start 14-Day Free Trial',
        perks: [
            {
                info: 'Unlock the Teams Dashboard',
                detail: null,
            },
            {
                info: '3 Pro Profiles Included',
                detail: `$${TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE} / month for additional`,
            },
            {
                info: 'Unlock Pro Profiles at a discount',
                detail: '25% off',
            },
            {
                info: '3 Team Members Included',
                detail: `$${TEAM_PLAN_ADDITIONAL_SEAT_PRICE} / month for additional`,
            },
            {
                info: 'Advanced Permissions Management',
                detail: 'Admin & View-Only access',
            },
        ],
    },
}

export const PRO_ADS_COMISSION = 0;

export const FREE_ADS_COMISSION = 0.1;

export const stage: "development" | "production" | "review" = process.env.NODE_ENV as "development" | "production" | "review";

export const isDev = stage !== "production";

export const SYMPHONY_WEBSITE_URL = isDev
    ? "http://localhost:3001"
    : stage === "review"
        ? "https://symphonyos.app"
        : "https://symphony.to";


export const CAMPAIGN_WIZARD_ROUTES = [
    "/marketing/new/music-streams",
    "/marketing/new/grow-playlist-followers",
    "/marketing/new/grow-instagram-engagement",
    "/marketing/new/playlists",
    "/marketing/new/video-views",
    "/marketing/new/tiktok-ads",
    "/marketing/new/presave",
    "/marketing/new/creatives",
    "/marketing/new/link-clicks",
    "/marketing/success",
];

export const FB_GEOGRAPHY_TARGETING_DEFAULTS = [
    {
        id: "touring-markets",
        description: "This audience automatically targets people in countries where you're likely to tour, including the US, Canada, and primary markets in Europe.",
        name: "Touring Markets",
        budget: 0.7,
        locations: keyBy(touringDefaultLocations, "value"),
    },
    {
        id: "global-markets",
        description: "This audience automatically targets people in countries where it's cheaper to drive a stream, to help build your global fanbase and drive your algorithmic streams.",
        name: "Global Markets",
        budget: 0.3,
        locations: keyBy(globalDefaultLocations, "value"),
    },
];

export const DEBOUNCE_SAVE_DRAFT_TIME = 3000

export const CURRENCY_SYMBOLS = {
    AED: 'د.إ',
    AFN: '؋',
    ALL: 'L',
    AMD: '֏',
    ANG: 'ƒ',
    AOA: 'Kz',
    ARS: '$',
    AUD: '$',
    AWG: 'ƒ',
    AZN: '₼',
    BAM: 'KM',
    BBD: '$',
    BDT: '৳',
    BGN: 'лв',
    BHD: '.د.ب',
    BIF: 'FBu',
    BMD: '$',
    BND: '$',
    BOB: '$b',
    BOV: 'BOV',
    BRL: 'R$',
    BSD: '$',
    // BTC: '₿',
    BTN: 'Nu.',
    BWP: 'P',
    BYN: 'Br',
    BYR: 'Br',
    BZD: 'BZ$',
    CAD: '$',
    CDF: 'FC',
    CHE: 'CHE',
    CHF: 'CHF',
    CHW: 'CHW',
    CLF: 'CLF',
    CLP: '$',
    CNH: '¥',
    CNY: '¥',
    COP: '$',
    COU: 'COU',
    CRC: '₡',
    CUC: '$',
    CUP: '₱',
    CVE: '$',
    CZK: 'Kč',
    DJF: 'Fdj',
    DKK: 'kr',
    DOP: 'RD$',
    DZD: 'دج',
    EEK: 'kr',
    EGP: '£',
    ERN: 'Nfk',
    ETB: 'Br',
    // ETH: 'Ξ',
    EUR: '€',
    FJD: '$',
    FKP: '£',
    GBP: '£',
    GEL: '₾',
    GGP: '£',
    GHC: '₵',
    GHS: 'GH₵',
    GIP: '£',
    GMD: 'D',
    GNF: 'FG',
    GTQ: 'Q',
    GYD: '$',
    HKD: '$',
    HNL: 'L',
    HRK: 'kn',
    HTG: 'G',
    HUF: 'Ft',
    IDR: 'Rp',
    ILS: '₪',
    IMP: '£',
    INR: '₹',
    IQD: 'ع.د',
    IRR: '﷼',
    ISK: 'kr',
    JEP: '£',
    JMD: 'J$',
    JOD: 'JD',
    JPY: '¥',
    KES: 'KSh',
    KGS: 'лв',
    KHR: '៛',
    KMF: 'CF',
    KPW: '₩',
    KRW: '₩',
    KWD: 'KD',
    KYD: '$',
    KZT: '₸',
    LAK: '₭',
    LBP: '£',
    LKR: '₨',
    LRD: '$',
    LSL: 'M',
    LTC: 'Ł',
    LTL: 'Lt',
    LVL: 'Ls',
    LYD: 'LD',
    MAD: 'MAD',
    MDL: 'lei',
    MGA: 'Ar',
    MKD: 'ден',
    MMK: 'K',
    MNT: '₮',
    MOP: 'MOP$',
    MRO: 'UM',
    MRU: 'UM',
    MUR: '₨',
    MVR: 'Rf',
    MWK: 'MK',
    MXN: '$',
    MXV: 'MXV',
    MYR: 'RM',
    MZN: 'MT',
    NAD: '$',
    NGN: '₦',
    NIO: 'C$',
    NOK: 'kr',
    NPR: '₨',
    NZD: '$',
    OMR: '﷼',
    PAB: 'B/.',
    PEN: 'S/.',
    PGK: 'K',
    PHP: '₱',
    PKR: '₨',
    PLN: 'zł',
    PYG: 'Gs',
    QAR: '﷼',
    RMB: '￥',
    RON: 'lei',
    RSD: 'Дин.',
    RUB: '₽',
    RWF: 'R₣',
    SAR: '﷼',
    SBD: '$',
    SCR: '₨',
    SDG: 'ج.س.',
    SEK: 'kr',
    SGD: 'S$',
    SHP: '£',
    SLL: 'Le',
    SOS: 'S',
    SRD: '$',
    SSP: '£',
    STD: 'Db',
    STN: 'Db',
    SVC: '$',
    SYP: '£',
    SZL: 'E',
    THB: '฿',
    TJS: 'SM',
    TMT: 'T',
    TND: 'د.ت',
    TOP: 'T$',
    TRL: '₤',
    TRY: '₺',
    TTD: 'TT$',
    TVD: '$',
    TWD: 'NT$',
    TZS: 'TSh',
    UAH: '₴',
    UGX: 'USh',
    USD: '$',
    UYI: 'UYI',
    UYU: '$U',
    UYW: 'UYW',
    UZS: 'лв',
    VEF: 'Bs',
    VES: 'Bs.S',
    VND: '₫',
    VUV: 'VT',
    WST: 'WS$',
    XAF: 'FCFA',
    // XBT: 'Ƀ',
    XCD: '$',
    XOF: 'CFA',
    XPF: '₣',
    XSU: 'Sucre',
    XUA: 'XUA',
    YER: '﷼',
    ZAR: 'R',
    ZMW: 'ZK',
    ZWD: 'Z$',
    ZWL: '$'
}

export enum Severity {
    WARNING = 'warning',
    ERROR = 'error',
}

export type SnackBarType = "SUCCESS" | "ERROR" | "INFO";
export type SeverityType = "success" | "info" | "warning" | "error";

export enum CheckoutType {
    UPGRADE_PLAN = 'upgradePlan',
    ONE_OFF_CAMPAIGN = 'oneOffCampaign',
}

export const YouTubeCampaignTypes = [
    {
        id: "default",
        name: "⚡️ Recommended",
        text: "Run your video ads with Symphony's recommended approach.",
        enabled: false
    },
    {
        id: "views",
        name: "📈 Cheapest Views",
        text: "Run your video ads to drive as many views as possible on your video.",
        enabled: true,
        adFormat: "YouTube Pre-roll Ads (Skippable)"
    },
    {
        id: "engagement",
        name: "🗣 More Engagement",
        text: "Run your video ads focused on driving more engagement to your video, including likes, comments, and subscribers.",
        enabled: true,
        adFormat: "YouTube In-feed Discovery Ads"
    },
]

export const YouTubeCampaignGeographyTypes = [
    {
        id: "english",
        name: "🌎 English-speaking Markets",
        text: "Run your video ads only in countries where English is the primary language.",
        subtitle: "This includes markets like the US, Canada, UK, and Australia. Average cost-per-view is 4 - 6 cents USD."
    },
    {
        id: "global",
        name: "🌐 Global Markets",
        text: "Run your video ads globally, everywhere YouTube is available.",
        subtitle: "This includes markets like India, Mexico, Brazil, etc. Average cost-per-view is 1 cent USD or less."
    },
    {
        id: "custom",
        name: "📍 Custom",
        text: "Run your video ads in specific countries.",
        subtitle: null
    },
]

export const REVIEW = "Review"
export const REVIEW_YOUR_CAMPAIGN = "Review your campaign"
export const NEXT = "Next"
//TODO: Change by the real quotes and images
export const TESTIMONIALS = [
    {
        action: "Grew by",
        quantity: "400%",
        units: "monthly listeners",
        testimony:
            "For any artist serious about growing their fanbase, Symphony is a must. Their ability to automate ad campaigns and extract valuable insight gives users a unique blend of dependability & simplicity.",
        witness: "West",
        occupation: "Rapper",
        picture:
            "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/testimonials/artist-west.jpeg",
    },
    {
        action: "Manages",
        quantity: "5 artists",
        units: "using Symphony",
        testimony:
            "Symphony is an incredible resource for today's music entrepreneur and artist. As a full time artist manager for 4 artists and the CEO of a record label, my goal everyday is to promote my artist's music and scale their careers as quickly as possible, with long-term career sustainability in mind.",
        witness: "Ashley",
        occupation: "CEO, Breakroom17",
        picture:
            "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/testimonials/ceo-ashley-breakroom17.png",
    },
    {
        action: "Grown to",
        quantity: "25k",
        units: "monthly listeners on Spotify",
        testimony:
            "Symphony is a necessity for all artists. Their automated ad campaigns and other marketing tools have allowed me to grow my following and listeners across all platforms!",
        witness: "Christof Hunte",
        occupation: "Pop Artist",
        picture:
            "https://symphonyos-public-assets.s3.us-east-2.amazonaws.com/images/testimonials/artist-christof-hunte.png",
    },
]

export enum COMMON_WIZARD_STEPS {
    FINALIZATION = "review",
    BUDGET = "budget",
}

export interface Stat {
    title: string;
    subtitle?: string;
    value: string | number;
    isCurrency?: boolean;
    currency?: string;
    icon?: string;
}

export const NAV_BAR_WIDTH_COLLAPSED = 64;
export const NAV_BAR_WIDTH_EXPANDED = 240;
export const NAV_BAR_HEIGHT_MOBILE = 56;
export const WEBSITE_TOP_BAR_HEIGHT = 64;
export const WEBSITE_TOP_BAR_HEIGHT_MOBILE = 50;
export const WEBSITE_BOTTOM_BAR_HEIGHT = 72;
export const PREV_NEXT_FOOTER_HEIGHT = 72;
export const PREV_NEXT_FOOTER_HEIGHT_MOBILE = 60;
export const TOP_BAR_HEADER_FOOTER_HEIGHT = 72 + 72 + 64;
export const TOP_BAR_HEADER_FOOTER_HEIGHT_MOBILE = 50 + 50 + 60;

export const HIDDEN_NAVBAR_ROUTES = [
    '/subscribe',
    '/marketing2/new',
    '/marketing/new',
    '/add-artist',
    '/email',
]

export enum RecommendationType {
    RUN_IG_ADS = 'runIgAds',
    BUILD_WEBSITE = 'buildWebsite',
    RUN_YOUTUBE_ADS = 'runYouTubeAds',
    CREATE_PRESAVE = 'createPresave',
}

export const welcomeToSymphonyRecommendations: Record<RecommendationType, {
    title: string,
    description: string,
    buttonText: string,
    image: string,
    imageBackgroundColor: string,
}> = {
    [RecommendationType.RUN_IG_ADS]: {
        title: 'Promote Your Music & Playlists on Instagram',
        description: 'Run AI-powered Instagram ads to reach your ideal fans and grow your streaming audiences.',
        buttonText: 'Run IG Ads',
        image: ads,
        imageBackgroundColor: '#E3EDFE',
    },
    [RecommendationType.CREATE_PRESAVE]: {
        title: 'Create a Pre-save Campaign',
        description: 'Boost followers, grow streams, and collect fan emails with Symphony\'s pre-save campaigns.',
        buttonText: 'Create a Pre-save',
        image: presave,
        imageBackgroundColor: '#C3DFD9',
    },
    [RecommendationType.BUILD_WEBSITE]: {
        title: 'Build Your Artist Website',
        description: 'Create a fully customizable home for your music, and collect deep analytics on your fanbase.',
        buttonText: 'Build a Website',
        image: website,
        imageBackgroundColor: '#FFA477',
    },
    [RecommendationType.RUN_YOUTUBE_ADS]: {
        title: 'Run YouTube Ads and Grow Views',
        description: 'Boost your video views with targeted TruView and Pre-roll ads on YouTube.',
        buttonText: 'Run YouTube Ads',
        image: ads,
        imageBackgroundColor: '#FAFAFA',
    },
}

export enum MARKETING_GOALS {
    TRACK_DATA = 'track_data',
    COLLECT_DATA = 'collect_data',
    LINK_IN_BIO = 'link_in_bio',
    GROW_STREAMS = 'grow_streams',
    GROW_SOCIALS = 'grow_socials',
    SELL_TICKETS = 'sell_tickets',
    SELL_MERCH = 'sell_merch',
    UNDERSTAND_DATA = 'understand_data',
}

export type MarketingGoalType = {
    type: MARKETING_GOALS,
    title: string
}

/** Enum defining all possible onboarding tab identifiers */
export enum OnboardingTab {
    ACCOUNT_TYPE = 'account_type',
    BASIC = 'basic',
    MARKETING_EXPERIENCE = 'marketing-experience',
    TEAMS_TOTAL_CREATORS = 'teams-total-creators',
    MARKETING_BUDGET = 'marketing-budget',
    MARKETING_GOALS = 'marketing-goals',
    WELCOME = 'welcome'
}

export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc',
}

export const MEMBER_ROLES_NAMES: Record<MemberRole, { name: string; priority: number }> = {
    owner: {
        name: 'Owner',
        priority: 0
    },
    administrator: {
        name: 'Team Admin',
        priority: 1
    },
    member: {
        name: 'Member',
        priority: 2
    },
};

export const USER_PERMISSION_ON_BRAND = {
    VIEW_ONLY: 'view_only',
    ADMINISTRATOR: 'administrator',
} as const

export enum OnboardingAccountTypes {
    CREATOR = 'creator',
    TEAM = 'team',
}

export const ONBOARDING_VERSION = 'welcome-to-symphony-jan-15-add-paywall-to-signup'

export enum CampaignStatus {
    ACTIVE = 'ACTIVE',
    ANTICIPATED_PAUSE = 'ANTICIPATED_PAUSE',
    DENIED = 'DENIED',
    DRAFT = 'DRAFT',
    ERROR = 'ERROR',
    FINISHED = 'FINISHED',
    IN_PROGRESS = 'IN_PROGRESS',
    IN_REVIEW = 'IN_REVIEW',
    NEEDS_ACTION = 'NEEDS_ACTION',
    NOT_FOUND = 'NOT_FOUND',
    PAUSED = 'PAUSED',
    PUBLISHED = 'PUBLISHED',
    SCHEDULED = 'SCHEDULED',
    STOPPED = 'STOPPED',
    SUBMITTED = 'SUBMITTED',
    TO_RETRY = 'TO_RETRY',
    WITH_ISSUES = 'WITH_ISSUES',
    UNKNOWN = 'UNKNOWN',
}

export const CAMPAIGN_STATUS_INFO: Record<CampaignStatus, { title: string, color: string }> = {
    [CampaignStatus.ACTIVE]: {
        title: 'ACTIVE',
        color: 'green'
    },
    [CampaignStatus.ANTICIPATED_PAUSE]: {
        title: 'ANTICIPATED PAUSE',
        color: 'yellow'
    },
    [CampaignStatus.DENIED]: {
        title: 'DENIED',
        color: 'red'
    },
    [CampaignStatus.DRAFT]: {
        title: 'DRAFT',
        color: 'yellow'
    },
    [CampaignStatus.ERROR]: {
        title: 'ERROR',
        color: 'red'
    },
    [CampaignStatus.FINISHED]: {
        title: 'FINISHED',
        color: 'grey'
    },
    [CampaignStatus.IN_PROGRESS]: {
        title: 'IN PROGRESS',
        color: 'green'
    },
    [CampaignStatus.IN_REVIEW]: {
        title: 'IN REVIEW',
        color: 'yellow'
    },
    [CampaignStatus.NEEDS_ACTION]: {
        title: 'NEEDS ACTION',
        color: 'red'
    },
    [CampaignStatus.NOT_FOUND]: {
        title: 'NOT FOUND',
        color: 'grey'
    },
    [CampaignStatus.PAUSED]: {
        title: 'PAUSED',
        color: 'yellow'
    },
    [CampaignStatus.PUBLISHED]: {
        title: 'PUBLISHED',
        color: 'green'
    },
    [CampaignStatus.SCHEDULED]: {
        title: 'SCHEDULED',
        color: 'blue'
    },
    [CampaignStatus.STOPPED]: {
        title: 'STOPPED',
        color: 'red'
    },
    [CampaignStatus.SUBMITTED]: {
        title: 'SUBMITTED',
        color: 'green'
    },
    [CampaignStatus.TO_RETRY]: {
        // NOTE: We don't show TO RETRY to users to avoid confusion, so we show IN REVIEW instead
        title: 'IN REVIEW',
        color: 'yellow'
    },
    [CampaignStatus.WITH_ISSUES]: {
        title: 'NEEDS REVIEW',
        color: 'red'
    },
    [CampaignStatus.UNKNOWN]: {
        title: 'UNKNOWN',
        color: 'grey'
    },
}

export enum CampaignType {
    BOOST_INSTAGRAM = 'boost_instagram',
    EMAIL_FANBASE = 'email_fanbase',
    LINK_CLICKS = 'link_clicks',
    PRE_SAVE = 'pre_save',
    RECORD_STREAMS = 'record_streams',
}

export enum WebsiteType {
    RECORD_STREAMS = 'record_streams',
    PRESAVE = 'presave',
    DATA_COLLECTOR = 'data_collector',
    TOUR = 'tour',
}

export const WEBSITE_TYPE_INFO: Record<WebsiteType, {
    title: string,
    icon: string,
}> = {
    [WebsiteType.RECORD_STREAMS]: {
        title: 'Music Site',
        icon: 'musicSite',
    },
    [WebsiteType.PRESAVE]: {
        title: 'Presave Site',
        icon: 'presaveSite',
    },
    [WebsiteType.DATA_COLLECTOR]: {
        title: 'Collect Data Site',
        icon: 'dataCollectorSite',
    },
    [WebsiteType.TOUR]: {
        title: 'Events Site',
        icon: 'eventsSite',
    },
}

export enum PlaybookStepName {
    // "Old" - tighly coupled Playbook Steps
    GOAL_SELECTOR = 'Goal',
    TARGETING = 'Targeting',
    CONTENT = 'Content',
    CONNECTIONS = 'Connections',
    BUDGET = 'Budget',
    FINALIZATION = 'Review',

    RECORD_SELECTOR = 'Record',
    LINK_SELECTOR = 'Link',
    LINK_ENTRY = 'Link Entry',
    PLAYLIST_SELECTOR = 'Playlist',
    VIDEO_SELECTOR = 'Video',
    PRESAVE_SELECTOR = 'Presave',

    CREATIVE = 'Creative',

    // "New" Playbook Steps - more dynamic based on 
    // the first "promotable" selection
    PROMOTABLE = 'promotable',
    SONG_SELECTION = 'song-selection',
    PRESAVES_SELECTION = 'presaves-selection',
    EMAIL_LIST_SELECTION = 'email-list-selection',
    CONNECTION_DECISION = 'connection-decision',
    LOGIN_TO_PAGES = 'connection-selection',
    PLAYLIST_SELECTION = 'playlist-selection',
    IG_SOUND_SELECTION = 'ig-sound-selection',
    TIKTOK_SOUND_SELECTION = 'tiktok-sound-selection',
    CREATIVE_SELECTION = 'creative-selection',
    SELECTION = 'selection',
    ONE_CLICK_CAMPAIGN_GENERATION = 'one-click-campaign-generation',
    AUTHENTICATION = 'authentication',
}

export const PLAYBOOK_STEP_INFO: Partial<Record<PlaybookStepName, {
    title: string,
    description: string,
    icon: string,
}>> = {
    [PlaybookStepName.GOAL_SELECTOR]: {
        title: 'Goal Selector',
        description: 'Choose the goal you want to achieve with your campaign.',
        icon: 'goalSelector',
    },
    [PlaybookStepName.TARGETING]: {
        title: 'Targeting',
        description: 'Choose the audience you want to target with your campaign.',
        icon: 'targeting',
    },
    [PlaybookStepName.CONTENT]: {
        title: 'Content',
        description: 'Create the content you want to use in your campaign.',
        icon: 'creative',
    },
    [PlaybookStepName.CONNECTIONS]: {
        title: 'Connections',
        description: 'Connect your accounts to your campaign.',
        icon: 'connections',
    },
    [PlaybookStepName.BUDGET]: {
        title: 'Budget',
        description: 'Set the budget for your campaign.',
        icon: 'budget',
    },
    [PlaybookStepName.FINALIZATION]: {
        title: 'Review Your Campaign',
        description: 'Review your campaign before launching.',
        icon: 'finalization',
    },
    [PlaybookStepName.RECORD_SELECTOR]: {
        title: 'Record Selector',
        description: 'Choose the record you want to promote with your campaign.',
        icon: 'recordSelector',
    },
    [PlaybookStepName.LINK_SELECTOR]: {
        title: 'Link Selector',
        description: 'Choose the link you want to promote with your campaign.',
        icon: 'linkSelector',
    },
    [PlaybookStepName.LINK_ENTRY]: {
        title: 'Link Entry',
        description: 'Enter the link you want to promote with your campaign.',
        icon: 'linkEntry',
    },
    [PlaybookStepName.PLAYLIST_SELECTOR]: {
        title: 'Playlist Selector',
        description: 'Choose the playlist you want to promote with your campaign.',
        icon: 'playlistSelector',
    },
    [PlaybookStepName.VIDEO_SELECTOR]: {
        title: 'Video Selector',
        description: 'Choose the video you want to promote with your campaign.',
        icon: 'videoSelector',
    },
    [PlaybookStepName.PRESAVE_SELECTOR]: {
        title: 'Presave Selector',
        description: 'Choose the presave you want to promote with your campaign.',
        icon: 'presaveSelector',
    },
    [PlaybookStepName.CREATIVE]: {
        title: 'Creative',
        description: 'Create the creative you want to use in your campaign.',
        icon: 'creative',
    },
}

export enum PromotableItemTypes {
    MUSIC = 'music',
    POSTS = 'posts',
    WEBSITES = 'websites',
    VIDEOS = 'videos',
    EVENT = 'event',
    LINK = 'link',
    EMAIL = 'email',
}

export enum AdPlatform {
    META = 'meta',
    YOUTUBE = 'youtube',
    PLAYLISTS = 'playlists',
    TIKTOK = 'tiktok',
    PRESAVE = 'presave',
}

export enum PlaybookType {
    PRESAVE = 'presave',
    // Original Campaign Playbooks
    BOOST_INSTAGRAM_ENGAGEMENT = 'boost_instagram_engagement',
    GET_PLAYLISTED = 'get_playlisted',
    GROW_PLAYLIST_FOLLOWERS = 'grow_playlist_followers',
    INCREASE_LINK_CLICKS = 'increase_link_clicks',
    INCREASE_MUSIC_STREAMS = 'increase_music_streams',
    INCREASE_VIDEO_VIEWS = 'increase_video_views',

    // "New" Playbooks
    // Meta
    META_EMAIL_SIGNUPS = 'meta_email_signups',
    META_IG_SOUND = 'meta_ig_sound',
    META_PRESAVES = 'meta_presaves',
    META_TICKET_SALES = 'meta_ticket_sales',

    // TikTok
    TIKTOK_BOOST_ENGAGEMENT = 'tiktok_boost_engagement',
    TIKTOK_EXTERNAL_TRAFFIC = 'tiktok_external_traffic',
    TIKTOK_MUSIC_STREAMS = 'tiktok_music_streams',
    TIKTOK_SOUND = 'tiktok_sound',
}

export type PlaybookTypeInfo = {
    playbookType: PlaybookType,
    title: string,
    description: string,
    icon: string,
    type: PromotableItemTypes,
    adPlatform: AdPlatform,
    link?: string,
    linkSelectorTitle?: string,
    linkSelectorSubtitle?: string,
    color: string,
}

export const PLAYBOOK_TYPE_INFO: Record<PlaybookType | CampaignType, PlaybookTypeInfo> = {
    [PlaybookType.PRESAVE]: {
        playbookType: PlaybookType.PRESAVE,
        icon: 'preSave',
        title: 'Create a Presave',
        description: 'Create a presave campaign to build anticipation for your upcoming release',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.PRESAVE,
        link: '/marketing/new/presave',
        color: Colors.campaignBlue,
    },

    // Original Campaign Playbooks
    [PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT]: {
        playbookType: PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT,
        icon: 'boostInstagramEngagementV2',
        title: 'Boost Instagram Engagement',
        description: 'Grow your Instagram following with engaging ads that convert',
        type: PromotableItemTypes.POSTS,
        adPlatform: AdPlatform.META,
        color: Colors.campaignBlue,
    },
    [PlaybookType.GET_PLAYLISTED]: {
        playbookType: PlaybookType.GET_PLAYLISTED,
        icon: 'getPlaylistedV2',
        title: 'Pitch Playlists',
        description: 'Pitch vetted playlists to grow song streams.',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.PLAYLISTS,
        color: Colors.campaignOrange,
    },
    [PlaybookType.GROW_PLAYLIST_FOLLOWERS]: {
        playbookType: PlaybookType.GROW_PLAYLIST_FOLLOWERS,
        icon: 'growPlaylistFollowersV2',
        title: 'Grow your Playlist',
        description: 'Get more followers and streams on your playlist with optimized ads',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.META,
        color: Colors.campaignPurple,
    },
    [PlaybookType.INCREASE_LINK_CLICKS]: {
        playbookType: PlaybookType.INCREASE_LINK_CLICKS,
        icon: 'increaseLinkClicksV2',
        title: 'Grow your Website Traffic',
        description: 'Drive more visitors to your website with optimized traffic campaigns',
        linkSelectorTitle: 'Website Link',
        linkSelectorSubtitle: 'Enter the website link you want to promote.',
        type: PromotableItemTypes.LINK,
        adPlatform: AdPlatform.META,
        color: Colors.campaignGreen,
    },
    [PlaybookType.INCREASE_MUSIC_STREAMS]: {
        playbookType: PlaybookType.INCREASE_MUSIC_STREAMS,
        icon: 'increaseMusicStreamsV2',
        title: 'Grow Song or Album Streams',
        description: 'Drive more streams to your music with targeted ads that reach new listeners',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.META,
        link: '/marketing2/new/increase_music_streams',
        color: Colors.campaignOrange,
    },
    [PlaybookType.INCREASE_VIDEO_VIEWS]: {
        playbookType: PlaybookType.INCREASE_VIDEO_VIEWS,
        icon: 'increaseVideoViewsV2',
        title: 'Grow your Video Views',
        description: 'Drive more views to your videos with targeted promotion',
        type: PromotableItemTypes.VIDEOS,
        adPlatform: AdPlatform.YOUTUBE,
        link: '/marketing/new/video-views',
        color: Colors.campaignBlue,
    },

    // "New" Playbooks
    // Meta
    [PlaybookType.META_EMAIL_SIGNUPS]: {
        playbookType: PlaybookType.META_EMAIL_SIGNUPS,
        icon: 'metaEmailSignupsV2',
        title: 'Grow Newsletter Signups',
        description: 'Grow your newsletter signups with targeted ads that convert.',
        linkSelectorTitle: 'Signup Link',
        linkSelectorSubtitle: 'Enter the signup link for your newsletter or website.',
        type: PromotableItemTypes.EMAIL,
        adPlatform: AdPlatform.META,
        color: Colors.campaignGreen,
    },
    [PlaybookType.META_IG_SOUND]: {
        playbookType: PlaybookType.META_IG_SOUND,
        icon: 'metaIgSoundV2',
        title: 'Boost Instagram Sound',
        description: 'Boost your Instagram sound with targeted ads that convert.',
        linkSelectorTitle: 'Instagram Sound Link',
        linkSelectorSubtitle: 'Enter the link to the Instagram sound you want to promote.',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.META,
        color: Colors.campaignInstagram,
    },
    [PlaybookType.META_PRESAVES]: {
        playbookType: PlaybookType.META_PRESAVES,
        icon: 'metaPresavesV2',
        title: 'Boost Presaves',
        description: 'Boost your presaves with targeted ads that convert.',
        linkSelectorTitle: 'Boost Presaves',
        linkSelectorSubtitle: 'Create a Symphony Presave page to collect presaves, emails, and let fans opt-in to Forever Saves.',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.META,
        color: Colors.campaignPurple,
    },
    [PlaybookType.META_TICKET_SALES]: {
        playbookType: PlaybookType.META_TICKET_SALES,
        icon: 'metaTicketSalesV2',
        title: 'Boost Ticket Sales',
        description: 'Boost ticket sales with targeted ads that convert',
        linkSelectorTitle: 'Ticket Link',
        linkSelectorSubtitle: 'Enter a link to promote ticket sales.',
        type: PromotableItemTypes.EVENT,
        adPlatform: AdPlatform.META,
        color: Colors.campaignRed,
    },

    // TikTok
    [PlaybookType.TIKTOK_BOOST_ENGAGEMENT]: {
        playbookType: PlaybookType.TIKTOK_BOOST_ENGAGEMENT,
        icon: 'tiktokEngagementV2',
        title: 'Boost TikTok Engagement',
        description: 'Grow your TikTok following with engaging ads that convert',
        type: PromotableItemTypes.POSTS,
        adPlatform: AdPlatform.TIKTOK,
        color: Colors.campaignBlue,
        link: '/marketing2/new/tiktok_boost_engagement',
    },
    [PlaybookType.TIKTOK_EXTERNAL_TRAFFIC]: {
        playbookType: PlaybookType.TIKTOK_EXTERNAL_TRAFFIC,
        icon: 'tiktokExternalTrafficV2',
        title: 'Grow Traffic with TikTok',
        description: 'Grow your traffic with targeted ads that convert',
        linkSelectorTitle: 'Website Link',
        linkSelectorSubtitle: 'Enter the link you want to promote.',
        type: PromotableItemTypes.LINK,
        adPlatform: AdPlatform.TIKTOK,
        color: Colors.campaignPurple,
    },
    [PlaybookType.TIKTOK_MUSIC_STREAMS]: {
        playbookType: PlaybookType.TIKTOK_MUSIC_STREAMS,
        icon: 'tiktokMusicStreamsV2',
        title: 'Grow Song or Album Streams',
        description: 'Grow song or album streams with targeted ads that convert',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.TIKTOK,
        color: Colors.campaignOrange,
    },
    [PlaybookType.TIKTOK_SOUND]: {
        playbookType: PlaybookType.TIKTOK_SOUND,
        icon: 'tiktokSoundV2',
        title: 'Promote a TikTok Sound',
        description: 'Enter a link to promote a sound on TikTok.',
        linkSelectorTitle: 'TikTok Sound Link',
        linkSelectorSubtitle: 'Enter a link to promote a sound on TikTok.',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.TIKTOK,
        color: Colors.campaignTikTok,
    },

    // OLD CAMPAIGN TYPES
    [CampaignType.RECORD_STREAMS]: {
        playbookType: PlaybookType.INCREASE_MUSIC_STREAMS,
        icon: 'increaseMusicStreamsV2',
        title: 'Grow Song or Album Streams',
        description: 'Drive more streams to your music with targeted ads that reach new listeners',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.META,
        link: '/marketing2/new/increase_music_streams',
        color: Colors.campaignOrange,
    },
    [CampaignType.BOOST_INSTAGRAM]: {
        playbookType: PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT,
        icon: 'boostInstagramEngagementV2',
        title: 'Boost Instagram Engagement',
        description: 'Grow your Instagram following with engaging ads that convert',
        type: PromotableItemTypes.POSTS,
        adPlatform: AdPlatform.META,
        color: Colors.campaignBlue,
    },
    [CampaignType.LINK_CLICKS]: {
        playbookType: PlaybookType.INCREASE_LINK_CLICKS,
        icon: 'increaseLinkClicksV2',
        title: 'Grow your Website Traffic',
        description: 'Drive more visitors to your website with optimized traffic campaigns',
        type: PromotableItemTypes.LINK,
        adPlatform: AdPlatform.META,
        color: Colors.campaignGreen,
    },
    [CampaignType.EMAIL_FANBASE]: {
        playbookType: PlaybookType.META_EMAIL_SIGNUPS,
        icon: 'metaEmailSignupsV2',
        title: 'Grow Newsletter Signups',
        description: 'Grow your newsletter signups with targeted ads that convert.',
        type: PromotableItemTypes.EMAIL,
        adPlatform: AdPlatform.META,
        color: Colors.campaignGreen,
    },
    [CampaignType.PRE_SAVE]: {
        playbookType: PlaybookType.META_PRESAVES,
        icon: 'metaPresavesV2',
        title: 'Boost Presaves',
        description: 'Boost your presaves with targeted ads that convert.',
        type: PromotableItemTypes.MUSIC,
        adPlatform: AdPlatform.PRESAVE,
        color: Colors.campaignPurple,
    },
}

export const OLD_CAMPAIGN_TYPES_FILTER = Object.values(CampaignType).reduce((acc, type) => {
    acc[type] = true;
    return acc;
}, {} as Record<CampaignType, boolean>);

export const enum SocialConnectType {
    SPOTIFY = 'spotify',
    YOUTUBE = 'youtube',
    INSTAGRAM = 'instagram',
    TIKTOK = 'tiktok',
    FACEBOOK = 'facebook',
    SOUNDCLOUD = 'soundcloud',
    AUDIOMACK = 'audiomack',
    DEEZER = 'deezer'
}

export const enum AdConnectionType {
    TIKTOK_AD_ACCOUNT = 'tiktok_ad_account',
    FACEBOOK_AD_ACCOUNT = 'facebook_ad_account',
    FACEBOOK_PAGE = 'facebook_page',
    INSTAGRAM_PAGE = 'instagram_page',
    FACEBOOK_PIXEL = 'facebook_pixel',
    FACEBOOK_CONVERSION_EVENT = 'facebook_conversion_event',
}

export const enum StreamingPlatform {
    SPOTIFY = 'spotify',
    APPLE_MUSIC = 'apple_music',
    YOUTUBE = 'youtube',
    DEEZER = 'deezer',
    SOUNDCLOUD = 'soundcloud',
    AUDIOMACK = 'audiomack',
    ANGHAMI = 'anghami',
    YANDEX = 'yandex',
    TIDAL = 'tidal',
    NAPSTER = 'napster',
    PANDORA = 'pandora',
    SHAZAM = 'shazam',
    AMAZON_MUSIC = 'amazon_music',
    LINK = 'link',
    BOOMPLAY = 'boomplay',
}

export const enum SocialPlatform {
    EMAIL = 'email',
    INSTAGRAM = 'instagram',
    TIKTOK = 'tiktok',
    FACEBOOK = 'facebook',
    TWITTER = 'twitter',
    X = 'x',
    YOUTUBE = 'youtube',
    LINKEDIN = 'linkedin',
}

export const STREAMING_PLATFORMS_INFO: Record<string, {
    title: string,
    icon: string,
    iconColor?: string,
    iconFlat?: string,
    iconPlatform?: string,
    backgroundColor?: string,
}> = {
    [StreamingPlatform.AMAZON_MUSIC]: {
        title: 'Amazon Music',
        icon: 'amazonMusic',
        iconColor: 'amazonMusicColor',
        iconFlat: 'amazonMusicFlat',
        iconPlatform: 'amazonMusicPlatform',
        backgroundColor: Colors.amazonMusic,
    },
    [StreamingPlatform.ANGHAMI]: {
        title: 'Anghami',
        icon: 'anghami',
        iconColor: 'anghami',
        iconFlat: 'anghami',
        iconPlatform: 'anghamiPlatform',
        backgroundColor: Colors.anghami,
    },
    [StreamingPlatform.APPLE_MUSIC]: {
        title: 'Apple Music',
        icon: 'appleMusic',
        iconColor: 'appleMusicColor',
        iconFlat: 'appleFlat',
        iconPlatform: 'appleMusicPlatform',
        backgroundColor: Colors.appleMusic,
    },
    [StreamingPlatform.AUDIOMACK]: {
        title: 'Audiomack',
        icon: 'audiomack',
        iconColor: 'audiomackColor',
        iconFlat: 'audiomackFlat',
        iconPlatform: 'audiomackPlatform',
        backgroundColor: Colors.audiomack,
    },
    [StreamingPlatform.DEEZER]: {
        title: 'Deezer',
        icon: 'deezer',
        iconColor: 'deezerColor',
        iconFlat: 'deezerFlat',
        iconPlatform: 'deezerPlatform',
        backgroundColor: Colors.deezer,
    },
    [StreamingPlatform.LINK]: {
        title: 'Link',
        icon: 'link',
        iconColor: 'link',
        iconFlat: 'linkFlat',
        iconPlatform: 'linkPlatform',
        backgroundColor: Colors.link,
    },
    [StreamingPlatform.NAPSTER]: {
        title: 'Napster',
        icon: 'music',
        iconColor: 'music',
        iconFlat: 'music',
        iconPlatform: 'music',
        backgroundColor: Colors.napster,
    },
    [StreamingPlatform.PANDORA]: {
        title: 'Pandora',
        icon: 'pandora',
        iconColor: 'pandora',
        iconFlat: 'pandoraFlat',
        iconPlatform: 'pandoraPlatform',
        backgroundColor: Colors.pandora,
    },
    [StreamingPlatform.SHAZAM]: {
        title: 'Shazam',
        icon: 'shazamColor',
        iconColor: 'shazamColor',
        iconFlat: 'shazamFlat',
        iconPlatform: 'shazamFlat',
        backgroundColor: Colors.shazam,
    },
    [StreamingPlatform.SOUNDCLOUD]: {
        title: 'SoundCloud',
        icon: 'soundcloud',
        iconColor: 'soundcloudColor',
        iconFlat: 'soundCloudFlat',
        iconPlatform: 'soundcloudPlatform',
        backgroundColor: Colors.soundcloud,
    },
    [StreamingPlatform.SPOTIFY]: {
        title: 'Spotify',
        icon: 'spotify',
        iconColor: 'spotifyColor',
        iconFlat: 'spotifyFlat',
        iconPlatform: 'spotifyPlatform',
        backgroundColor: Colors.spotify,
    },
    [StreamingPlatform.TIDAL]: {
        title: 'Tidal',
        icon: 'tidal',
        iconColor: 'tidal',
        iconFlat: 'tidal',
        iconPlatform: 'tidalPlatform',
        backgroundColor: Colors.tidal,
    },
    [StreamingPlatform.YANDEX]: {
        title: 'Yandex',
        icon: 'yandex',
        iconColor: 'yandex',
        iconFlat: 'yandexFlat',
        iconPlatform: 'yandexPlatform',
        backgroundColor: Colors.yandex,
    },
    [StreamingPlatform.YOUTUBE]: {
        title: 'YouTube',
        icon: 'youtube',
        iconColor: 'youtubeColor',
        iconFlat: 'youtubeFlat',
        iconPlatform: 'youtubePlatform',
        backgroundColor: Colors.youtube,
    },
}

export const SOCIAL_PLATFORMS_INFO: Record<string, {
    title: string,
    icon: string,
    iconColor?: string,
    iconFlat: string,
    backgroundColor?: string,
}> = {
    [SocialPlatform.INSTAGRAM]: {
        title: 'Instagram',
        icon: 'instagram',
        iconColor: 'instagramColor',
        iconFlat: 'instagramFlat',
        backgroundColor: Colors.instagram,
    },
    [SocialPlatform.TIKTOK]: {
        title: 'TikTok',
        icon: 'tiktok',
        iconColor: 'tiktokColor',
        iconFlat: 'tiktokFlat',
        backgroundColor: Colors.tiktok,
    },
    [SocialPlatform.FACEBOOK]: {
        title: 'Facebook',
        icon: 'facebook',
        iconColor: 'facebookColor',
        iconFlat: 'facebookFlat',
        backgroundColor: Colors.facebook,
    },
    [SocialPlatform.TWITTER]: {
        title: 'X',
        icon: 'xColor',
        iconColor: 'xColor',
        iconFlat: 'xFlat',
        backgroundColor: Colors.twitter,
    },
    [SocialPlatform.X]: {
        title: 'X',
        icon: 'xColor',
        iconColor: 'xColor',
        iconFlat: 'xFlat',
        backgroundColor: Colors.x,
    },
    [SocialPlatform.YOUTUBE]: {
        title: 'YouTube',
        icon: 'youtube',
        iconColor: 'youtubeColor',
        iconFlat: 'youtubeFlat',
        backgroundColor: Colors.youtube,
    },
    [SocialPlatform.LINKEDIN]: {
        title: 'LinkedIn',
        icon: 'linkedin',
        iconColor: 'linkedinColor',
        iconFlat: 'linkedinFlat',
        backgroundColor: Colors.linkedin,
    },
    [SocialPlatform.EMAIL]: {
        title: 'Email',
        icon: 'mail',
        iconColor: 'mail',
        iconFlat: 'mail',
        backgroundColor: Colors.email,
    },
}

export const TIMEZONES = {
    "Pacific/Midway": "Midway Island, Samoa",
    "Pacific/Honolulu": "Hawaii",
    "America/Juneau": "Alaska",
    "America/Boise": "Mountain Time",
    "America/Dawson": "Dawson, Yukon",
    "America/Chihuahua": "Chihuahua, La Paz, Mazatlan",
    "America/Phoenix": "Arizona",
    "America/Chicago": "Central Time",
    "America/Regina": "Saskatchewan",
    "America/Mexico_City": "Guadalajara, Mexico City, Monterrey",
    "America/Belize": "Central America",
    "America/Detroit": "Eastern Time",
    "America/Bogota": "Bogota, Lima, Quito",
    "America/Caracas": "Caracas, La Paz",
    "America/Santiago": "Santiago",
    "America/St_Johns": "Newfoundland and Labrador",
    "America/Sao_Paulo": "Brasilia",
    "America/Tijuana": "Tijuana",
    "America/Montevideo": "Montevideo",
    "America/Argentina/Buenos_Aires": "Buenos Aires, Georgetown",
    "America/Godthab": "Greenland",
    "America/Los_Angeles": "Pacific Time",
    "Atlantic/Azores": "Azores",
    "Atlantic/Cape_Verde": "Cape Verde Islands",
    "GMT": "UTC",
    "Europe/London": "Edinburgh, London",
    "Europe/Dublin": "Dublin",
    "Europe/Lisbon": "Lisbon",
    "Africa/Casablanca": "Casablanca, Monrovia",
    "Atlantic/Canary": "Canary Islands",
    "Europe/Belgrade": "Belgrade, Bratislava, Budapest, Ljubljana, Prague",
    "Europe/Sarajevo": "Sarajevo, Skopje, Warsaw, Zagreb",
    "Europe/Brussels": "Brussels, Copenhagen, Madrid, Paris",
    "Europe/Amsterdam": "Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
    "Africa/Algiers": "West Central Africa",
    "Europe/Bucharest": "Bucharest",
    "Africa/Cairo": "Cairo",
    "Europe/Helsinki": "Helsinki, Kiev, Riga, Sofia, Tallinn, Vilnius",
    "Europe/Athens": "Athens, Minsk",
    "Asia/Jerusalem": "Jerusalem",
    "Africa/Harare": "Harare, Pretoria",
    "Europe/Moscow": "Istanbul, Moscow, St. Petersburg, Volgograd",
    "Asia/Kuwait": "Kuwait, Riyadh",
    "Africa/Nairobi": "Nairobi",
    "Asia/Baghdad": "Baghdad",
    "Asia/Tehran": "Tehran",
    "Asia/Dubai": "Abu Dhabi, Muscat",
    "Asia/Baku": "Baku, Tbilisi, Yerevan",
    "Asia/Kabul": "Kabul",
    "Asia/Yekaterinburg": "Ekaterinburg",
    "Asia/Karachi": "Islamabad, Karachi, Tashkent",
    "Asia/Kolkata": "Chennai, Kolkata, Mumbai, New Delhi",
    "Asia/Kathmandu": "Kathmandu",
    "Asia/Dhaka": "Astana, Dhaka",
    "Asia/Colombo": "Sri Jayawardenepura",
    "Asia/Almaty": "Almaty, Novosibirsk",
    "Asia/Rangoon": "Yangon Rangoon",
    "Asia/Bangkok": "Bangkok, Hanoi, Jakarta",
    "Asia/Krasnoyarsk": "Krasnoyarsk",
    "Asia/Shanghai": "Beijing, Chongqing, Hong Kong SAR, Urumqi",
    "Asia/Kuala_Lumpur": "Kuala Lumpur, Singapore",
    "Asia/Taipei": "Taipei",
    "Australia/Perth": "Perth",
    "Asia/Irkutsk": "Irkutsk, Ulaanbaatar",
    "Asia/Seoul": "Seoul",
    "Asia/Tokyo": "Osaka, Sapporo, Tokyo",
    "Asia/Yakutsk": "Yakutsk",
    "Australia/Darwin": "Darwin",
    "Australia/Adelaide": "Adelaide",
    "Australia/Sydney": "Canberra, Melbourne, Sydney",
    "Australia/Brisbane": "Brisbane",
    "Australia/Hobart": "Hobart",
    "Asia/Vladivostok": "Vladivostok",
    "Pacific/Guam": "Guam, Port Moresby",
    "Asia/Magadan": "Magadan, Solomon Islands, New Caledonia",
    "Asia/Kamchatka": "Kamchatka, Marshall Islands",
    "Pacific/Fiji": "Fiji Islands",
    "Pacific/Auckland": "Auckland, Wellington",
    "Pacific/Tongatapu": "Nuku'alofa",
};

export const fontThemeStyles: Record<string, FontThemeStyle> = {
    font1: {
        title: "DIN",
        name: "font1",
        order: 1,
        isPaidOnly: false,
    },
    font4: {
        title: "Baskerville",
        name: "font4",
        order: 4,
        isPaidOnly: true,
    },
    font5: {
        title: "Kode Mono",
        name: "font5",
        order: 5,
        isPaidOnly: true,
    },
    font6: {
        title: "Lato",
        name: "font6",
        order: 6,
        isPaidOnly: true,
    },
    font7: {
        title: "DM Serif Display",
        name: "font7",
        order: 7,
        isPaidOnly: true,
    },
    font8: {
        title: "Lobster",
        name: "font8",
        order: 8,
        isPaidOnly: true,
    },
    font9: {
        title: "Dosis",
        name: "font9",
        order: 9,
        isPaidOnly: true,
    },
    font10: {
        title: "Comfortaa",
        name: "font10",
        order: 10,
        isPaidOnly: true,
    },
    font11: {
        title: "Jacquarda Bastarda 9",
        name: "font11",
        order: 11,
        isPaidOnly: true,
    },
    font12: {
        title: "Slackside One",
        name: "font12",
        order: 12,
        isPaidOnly: true,
    },
    font2: {
        title: "Times New Roman",
        name: "font2",
        order: 15,
        isPaidOnly: true,
    },
    font3: {
        title: "Helvetica",
        name: "font3",
        order: 16,
        isPaidOnly: true,
    },
} as const;


export const DESCRIPTION_INIT_TEXT = `Setup your account and grow your fanbase with Symphony.`

export const enum WebsiteTemplateCategory {
    ALL_TEMPLATES = 'allTemplates',
    MUSIC = 'musicContent',
    EVENTS = 'eventsContent',
    COLLECT_DATA = 'collectDataContent',
    POPULAR = 'popularContent',
    GALLERY = 'galleryContent',
}

export const enum WebsiteBuilderTemplateType {
    BLANK = 'blank',
    BASIC = 'basic',

    MUSIC_1 = 'music_1',
    MUSIC_2 = 'music_2',
    MUSIC_3 = 'music_3',
    MUSIC_4 = 'music_4',

    COLLECT_DATA_1 = 'collect_data_1',
    COLLECT_DATA_2 = 'collect_data_2',
    COLLECT_DATA_3 = 'collect_data_3',
    COLLECT_DATA_4 = 'collect_data_4',

    TOUR_1 = 'tour_1',
    TOUR_2 = 'tour_2',
    TOUR_3 = 'tour_3',
    TOUR_4 = 'tour_4',

    PRESAVE = 'presave',
}

export const WEBSITE_TEMPLATE_INFO: Record<WebsiteBuilderTemplateType, {
    websiteTemplateCategory: WebsiteTemplateCategory;
    title: string;
    isDisabled?: boolean;
    isFree?: boolean;
    isPopular?: boolean;
}> = {
    [WebsiteBuilderTemplateType.BLANK]: {
        websiteTemplateCategory: WebsiteTemplateCategory.ALL_TEMPLATES,
        title: 'Blank',
        isFree: true,
    },
    [WebsiteBuilderTemplateType.BASIC]: {
        websiteTemplateCategory: WebsiteTemplateCategory.ALL_TEMPLATES,
        title: 'Basic',
        isFree: true,
    },

    [WebsiteBuilderTemplateType.MUSIC_1]: {
        websiteTemplateCategory: WebsiteTemplateCategory.MUSIC,
        title: 'Music 1',
        isFree: true,
        isPopular: true,
    },
    [WebsiteBuilderTemplateType.MUSIC_2]: {
        websiteTemplateCategory: WebsiteTemplateCategory.MUSIC,
        title: 'Music 2',
        isPopular: true,
    },
    [WebsiteBuilderTemplateType.MUSIC_3]: {
        websiteTemplateCategory: WebsiteTemplateCategory.MUSIC,
        title: 'Music 3',
    },
    [WebsiteBuilderTemplateType.MUSIC_4]: {
        websiteTemplateCategory: WebsiteTemplateCategory.MUSIC,
        title: 'Music 4',
    },

    [WebsiteBuilderTemplateType.TOUR_1]: {
        websiteTemplateCategory: WebsiteTemplateCategory.EVENTS,
        title: 'Tour 1',
        isFree: true,
        isPopular: true,
    },
    [WebsiteBuilderTemplateType.TOUR_2]: {
        websiteTemplateCategory: WebsiteTemplateCategory.EVENTS,
        title: 'Tour 2',
    },
    [WebsiteBuilderTemplateType.TOUR_3]: {
        websiteTemplateCategory: WebsiteTemplateCategory.EVENTS,
        title: 'Tour 3',
    },
    [WebsiteBuilderTemplateType.TOUR_4]: {
        websiteTemplateCategory: WebsiteTemplateCategory.EVENTS,
        title: 'Tour 4',
    },

    [WebsiteBuilderTemplateType.COLLECT_DATA_1]: {
        websiteTemplateCategory: WebsiteTemplateCategory.COLLECT_DATA,
        title: 'Collect Data 1',
        isFree: true,
        isPopular: true,
    },
    [WebsiteBuilderTemplateType.COLLECT_DATA_2]: {
        websiteTemplateCategory: WebsiteTemplateCategory.COLLECT_DATA,
        title: 'Collect Data 2',
    },
    [WebsiteBuilderTemplateType.COLLECT_DATA_3]: {
        websiteTemplateCategory: WebsiteTemplateCategory.COLLECT_DATA,
        title: 'Collect Data 3',
    },
    [WebsiteBuilderTemplateType.COLLECT_DATA_4]: {
        websiteTemplateCategory: WebsiteTemplateCategory.COLLECT_DATA,
        title: 'Collect Data 4',
    },

    [WebsiteBuilderTemplateType.PRESAVE]: {
        websiteTemplateCategory: WebsiteTemplateCategory.POPULAR,
        title: 'Presave',
        isFree: true,
        isDisabled: true,
    },
}

export const WEBSITE_TEMPLATE_CATEGORIES: {
    icon: string;
    name: string;
    title: string;
    color: string;
    iconColor?: string;
}[] = [
        {
            icon: 'star',
            name: WebsiteTemplateCategory.POPULAR,
            title: 'Popular',
            color: Colors.purple,
            iconColor: Colors.yellow,
        },
        {
            icon: 'musicSite',
            name: WebsiteTemplateCategory.MUSIC,
            title: 'Music',
            color: Colors.purple,
        },
        {
            icon: 'dataCollectorSite',
            name: WebsiteTemplateCategory.COLLECT_DATA,
            title: 'Collect Data',
            color: Colors.purple,
        },
        {
            icon: 'tourSite',
            name: WebsiteTemplateCategory.EVENTS,
            title: 'Events',
            color: Colors.purple,
        },
        // {
        //     icon: '',
        //     name: WebsiteTemplateCategory.ALL_TEMPLATES,
        //     title: 'All Templates',
        //     color: Colors.purple,
        // },
        {
            icon: 'image',
            name: WebsiteTemplateCategory.GALLERY,
            title: 'Gallery',
            color: Colors.purple,
            iconColor: Colors.blue,
        },
    ]